import { useState, type ReactNode } from 'react';
import Typography from '@mui/material/Typography';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlineOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { AppDialog } from './AppDialog';
import { OverlayFooter } from './OverlayFooter';

export interface ConfirmDialogProps {
  open: boolean;
  title?: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** `error` for destructive actions (delete, discard). */
  tone?: 'primary' | 'error' | 'warning';
  /** May be async: the dialog stays open and busy until it settles. */
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

/**
 * Yes/no question. For a one-off question from code, prefer
 * `useConfirm()`, which renders this dialog for you.
 */
export function ConfirmDialog({
  open,
  title = 'Are you sure?',
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'primary',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const [pending, setPending] = useState(false);

  const confirm = async () => {
    setPending(true);
    try {
      await onConfirm();
    } finally {
      setPending(false);
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={onCancel}
      size="xs"
      busy={pending}
      title={title}
      icon={tone === 'primary' ? <HelpOutlineIcon /> : <WarningAmberOutlinedIcon color={tone} />}
      footer={
        <OverlayFooter
          cancelLabel={cancelLabel}
          primary={{ label: confirmLabel, tone, onClick: confirm, loading: pending, autoFocus: true }}
        />
      }
    >
      {description ? (
        <Typography component="div" variant="body2" color="text.secondary">
          {description}
        </Typography>
      ) : null}
    </AppDialog>
  );
}
